/**
 * HubSpot API Test Harness
 * 
 * Purpose: Development tool for testing HubSpot Private App API integration
 * 
 * Features:
 * - Search contacts by email
 * - Create new contacts
 * - Add notes to contacts
 * - Update contact properties (tags, custom fields)
 * 
 * Cloudflare Worker: hubspot-api-tester.andrew-kidd.workers.dev
 * - Located in Cloudflare Workers & Pages dashboard
 * - Uses HUBSPOT_ACCESS_TOKEN secret (Private App token)
 * - Handles HubSpot API calls with proper authentication
 * 
 * Usage:
 * 1. Navigate to /hubspot-test
 * 2. Enter test email/name
 * 3. Click buttons to test each API operation
 * 4. Check JSON responses for success/errors
 * 5. Verify in HubSpot CRM
 * 
 * Note: This is a development tool only. Keep for future HubSpot integration testing.
 */

import React, { useState } from 'react';

function HubSpotTest() {
  const [email, setEmail] = useState('test@example.com');
  const [firstName, setFirstName] = useState('Test');
  const [lastName, setLastName] = useState('User');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  const testAction = async (action) => {
    setLoading(true);
    setResponse(null);

    try {
      const res = await fetch('https://hubspot-api-tester.andrew-kidd.workers.dev/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, email, firstName, lastName })
      });

      const data = await res.json();
      setResponse(data);
    } catch (error) {
      setResponse({ error: error.message });
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-[#152C47]">HubSpot API Tester</h1>

        {/* Input Form */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-3 py-2 border rounded"
              />
            </div>
          </div>

          {/* Test Buttons */}
          <div className="flex gap-4 flex-wrap">
            <button
              onClick={() => testAction('search')}
              disabled={loading}
              className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
            >
              1. Search Contact
            </button>
            <button
              onClick={() => testAction('create')}
              disabled={loading}
              className="bg-green-500 text-white px-6 py-2 rounded hover:bg-green-600 disabled:opacity-50"
            >
              2. Create Contact
            </button>
            <button
              onClick={() => testAction('note')}
              disabled={loading}
              className="bg-purple-500 text-white px-6 py-2 rounded hover:bg-purple-600 disabled:opacity-50"
            >
              3. Add Note
            </button>
            <button
              onClick={() => testAction('tag')}
              disabled={loading}
              className="bg-orange-500 text-white px-6 py-2 rounded hover:bg-orange-600 disabled:opacity-50"
            >
              4. Add Tag
            </button>
          </div>
        </div>

        {/* Response Display */}
        {loading && (
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-gray-500">Loading...</p>
          </div>
        )}

        {response && (
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-4">Response:</h2>
            <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-96 text-sm">
              {JSON.stringify(response, null, 2)}
            </pre>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-6 bg-blue-50 p-6 rounded-lg">
          <h3 className="font-bold mb-2">Testing Instructions:</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>Click "Search Contact" to see if the email exists in HubSpot</li>
            <li>Click "Create Contact" to create a new contact (use a unique email)</li>
            <li>Click "Add Note" to add a test note to the contact (must exist first)</li>
            <li>Check the JSON response below each test</li>
            <li>Verify in HubSpot CRM that contacts/notes were created</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

export default HubSpotTest;